import React, { useRef, useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import { CustomButton, DateTimePicker, Loading, Textfield } from '../../_common';
import { Alert, Col, Row, Table } from 'react-bootstrap';
import { isValidDate } from '../../../utils/helpers';
import { useQuery, useMutation } from '@apollo/client/react';
import {
    GET_JOB_SUMMARY_INFO,
    GET_LOAD_ALLOCATION,
    GET_LOAD_ALLOCATION_SUMMARY,
} from '../../../graphql/client/queries';
import { GetJobSummaryInfo, GetJobSummaryInfo_getJobSummaryInfo as JobSummaryInfo } from '../../../types/schemaTypes';
import './DailyTonnageModal.scss';
import { SAVE_TONNAGE } from '../../../graphql/client/mutations';

import {  ToastContainer, toast } from 'react-toastify';
import {toastOpt} from '../../../utils/helpers/toastHelper'

interface TonnageDetails {
    jobsummaryId: number | null;
    tonnage: number;
}

interface FieldErrors {
    fromDateError: string;
    toDateError: string;
    plannedTonnageError: string;
}

interface DailyTonnageModalProps {
    onClickHide: () => void;
    show: boolean;
    errorMessage?: string;
    jobId: number;
}

const toasterOptions = { duration: 2500 };

const Messages = {
    save: 'Scheduled successfully',
};
const ErrorMessages = {
    error: 'Failed to schedule',
};

const DailyTonnageModal = ({ show, onClickHide, errorMessage, jobId }: DailyTonnageModalProps): React.ReactElement => {
    const [details, setDetails] = useState<TonnageDetails[]>([]);
    const [inputs, setInputs] = useState({
        fromDate: new Date(''),
        toDate: new Date(''),
        tonnageDetails: details,
        tonsNeeded: 0,
    });
    const componentRef = useRef(null);

    const initialErrorsState: FieldErrors = {
        fromDateError: '',
        toDateError: '',
        plannedTonnageError: '',
    };

    const [fieldErrors, setFieldErrors] = useState(initialErrorsState);
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);

    const today = new Date();
    const [summaryFromDate] = useState(`${today.getMonth() + 1}-${today.getDate()}-${today.getFullYear()} 00:00:00`);
    const fourteenDaysFromToday = new Date();
    fourteenDaysFromToday.setDate(today.getDate() + 13);
    const [summaryToDate] = useState(
        `${fourteenDaysFromToday.getMonth() +
            1}-${fourteenDaysFromToday.getDate()}-${fourteenDaysFromToday.getFullYear()} 00:00:00`,
    );

    const { loading: jobSummaryLoading, data: jobSummaryData } = useQuery<GetJobSummaryInfo>(GET_JOB_SUMMARY_INFO, {
        variables: { jobId: jobId },
        fetchPolicy: 'network-only',
    });

    const jobSummaries: JobSummaryInfo[] = [];

    useEffect(() => {
        // Enable save only when both dates are selected
        const hasBothDates = isValidDate(inputs.fromDate) && isValidDate(inputs.toDate);
        const hasNoFieldErrors = !fieldErrors.fromDateError && !fieldErrors.toDateError;
        const shouldEnableSave = hasBothDates && hasNoFieldErrors;
        setIsSaveDisabled(!shouldEnableSave);
    }, [details, inputs.fromDate, inputs.toDate, fieldErrors, jobSummaryData]);

    if (jobSummaryData && jobSummaryData.getJobSummaryInfo && jobSummaryData.getJobSummaryInfo.length !== 0) {
        jobSummaryData.getJobSummaryInfo.forEach((element: JobSummaryInfo | null): void => {
            if (element !== null) {
                jobSummaries.push(element);
            }
        });
    }

    const clearState = (): void => {
        setFieldErrors(initialErrorsState);
        setInputs({
            fromDate: new Date(''),
            toDate: new Date(''),
            tonnageDetails: [],
            tonsNeeded: 0,
        });
    };

    const onCancel = (): void => {
        clearState();
        onClickHide();
    };

    const handleOnChange = async (jobsummaryId: number | null, tonnage: number): Promise<void> => {
        fieldErrors.plannedTonnageError = '';
        const tempDetails = [...details];
        const tonSummary = { jobsummaryId, tonnage };
        let isNewTonSummary = true;
        for (const index in tempDetails) {
            if (tempDetails[index].jobsummaryId === jobsummaryId) {
                tempDetails[index] = tonSummary;
                isNewTonSummary = false;
                break;
            }
        }
        if (isNewTonSummary) {
            tempDetails?.push(tonSummary);
        }
        setDetails(tempDetails);
        setInputs({
            ...inputs,
            tonnageDetails: tempDetails, 
        });
    };

    const validatePlannedTonnage = (target: any): void => {
        const value = target.value;
        if (value && !/^[0-9]\d{0,5}(?:\.\d{1,2})?$/.test(value)) {
            target.parentNode.children[1].innerText = 'Tons should range from 0 to 999999.99';
        } else {
            target.parentNode.children[1].innerText = '';
        }
    };

    const validateRequiredFields = (): any => {
        const plannedTonnageError = '';
        let fromDateError = '';
        let toDateError = '';
        const fromDate = !!inputs.fromDate ? new Date(inputs.fromDate) : '';
        const toDate = !!inputs.toDate ? new Date(inputs.toDate) : '';
        
        if (!isValidDate(fromDate)) {
            fromDateError = 'Start date is required.';
        }
        if (!isValidDate(toDate)) {
            toDateError = 'End date is required';
        }
        setFieldErrors({
            ...fieldErrors,
            fromDateError,
            toDateError,
            plannedTonnageError,
        });

        return {
            toDateError,
            fromDateError,
            plannedTonnageError,
        };
    };

    const renderToast = (message, type = 'info') => {
        interface ToastProps {
            onClose?: () => void;
        }
        const Comp = (props: ToastProps) => {
            return (
                <Alert variant={type} onClose={props.onClose}>
                    {message}
                </Alert>
            );
        };

        Comp.displayName = 'toast';
        return Comp;
    };

    const onCompleted = (action: string) => {
        toast.success(Messages[action],toastOpt);
        onCancel();
    };

    const onActionError = (action: string) => {
        toast.error(ErrorMessages[action],toastOpt);
        onCancel();
    };

    const [saveTonnage] = useMutation(SAVE_TONNAGE, {

        onCompleted: () => onCompleted('save'),
        refetchQueries: [
            {
                query: GET_LOAD_ALLOCATION,
                variables: {
                    input: {
                        jobId: jobId,
                        fromDate: summaryFromDate,
                        toDate: summaryToDate,
                    },
                },
            },
            {
                query: GET_LOAD_ALLOCATION_SUMMARY,
                variables: {
                    input: {
                        jobId: jobId,
                        date: `${today.getMonth() + 1}-${today.getDate()}-${today.getFullYear()}`,
                    },
                },
            },
        ],

        onError: () => onActionError('error'),
    });

    const handleOnSave = () => {
        const { plannedTonnageError, fromDateError, toDateError } = validateRequiredFields();
        // const { fromDateError, toDateError } = fieldErrors;

        if (fromDateError === '' && toDateError === '' && plannedTonnageError == '') {
            const saveTonnageInput = {
                fromDate: inputs.fromDate ?? '',
                toDate: inputs.toDate ?? '',
                tonnageDetails: inputs.tonnageDetails.filter(detail => 
                    !isNaN(detail.tonnage) && detail.tonnage >= 0
                ),
            };
            saveTonnage({ variables: { input: saveTonnageInput } });
        }
    };

    const onSave = (): void => {
        handleOnSave();
    };

    const isDateBeforeToday = date => {
        return new Date(date.toDateString()) < new Date(new Date().toDateString());
    };

    const handleDateInputChange = (inputName: string, value: Date): void => {
        // Convert to Date object if not null/undefined
        const dateValue = !!value ? new Date(value) : new Date('');
        
        const newInputs = {
            ...inputs,
            [inputName]: dateValue,
        };
        setInputs(newInputs);

        switch (inputName) {
            case 'fromDate': {
                const fromDate = dateValue;
                let fromDateError = '';
                if (!isValidDate(fromDate)) {
                    fromDateError = 'Please enter a valid date.';
                } else if (isDateBeforeToday(fromDate)) {
                    fromDateError = 'Start date should not be a previous date.';
                } else if (fromDate && newInputs.toDate && isValidDate(newInputs.toDate) && fromDate > newInputs.toDate) {
                    fromDateError = 'Start date should not be greater than end date.';
                }
                setFieldErrors(prevErrors => ({
                    ...prevErrors,
                    fromDateError,
                }));
                break;
            }
            case 'toDate': {
                const toDate = dateValue;
                let toDateError = '';

                if (!isValidDate(toDate)) {
                    toDateError = 'Please enter a valid date.';
                } else if (toDate && newInputs.fromDate && isValidDate(newInputs.fromDate) && toDate < newInputs.fromDate) {
                    toDateError = 'End date should not be less than start date.';
                }
                setFieldErrors(prevErrors => ({
                    ...prevErrors,
                    toDateError,
                }));
                break;
            }
        }
    };

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        event.persist();
        setInputs(inputs => ({ ...inputs, [event.target.name]: event.target.value }));
    };

    return (
        <Modal show={show} onHide={onCancel} dialogClassName="jobSummary-Container">
            <Modal.Header className="DailyTonnageModal-Header">
                <Modal.Title className="DailyTonnageModal-Header-Container">
                    <button className="material-icons DailyTonnageModal-X-Button" onClick={onCancel}>
                        close
                    </button>
                    <div className="DailyTonnageModal-Title">Set Daily Tonnage</div>
                    <div className="DailyTonnageModal-ErrorMessage">{errorMessage}</div>
                </Modal.Title>
            </Modal.Header>

            <div className="DailyTonnageModal-FormContainer">
            
                <Row>
                    <Col md={4}>
                        <DateTimePicker
                            dateOnly
                            label="START DATE"
                            value={inputs.fromDate}
                            onChange={(value: any) => {
                                handleDateInputChange('fromDate', value);
                            }}
                            error={fieldErrors.fromDateError || ''}
                            required
                        />
                    </Col>
                    <Col md={4}>
                        <DateTimePicker
                            dateOnly
                            label="END DATE"
                            value={inputs.toDate}
                            onChange={(value: any) => {
                                handleDateInputChange('toDate', value);
                            }}
                            error={fieldErrors.toDateError || ''}
                            required
                        />
                    </Col>
                </Row>
                <Row className="DailyTonnage-Container">
                    <Row className="JobSummary-Container">
                        {jobSummaryLoading ? (
                            <div className="JobSummary-Spinner-Container">
                                <Loading />
                            </div>
                        ) : jobSummaries.length > 0 ? (
                            <Table
                                style={{ width: 850 }}
                                striped
                                bordered
                                hover
                                ref={componentRef}
                                className="tabletest"
                            >
                                <thead style={{ fontWeight: 'bolder' }}>
                                    <tr>
                                        <th>Proppant</th>
                                        <th>Lofa</th>
                                        <th>Sand PO</th>
                                        <th>Transportation PO</th>
                                        <th>Tons Needed</th>
                                    </tr>
                                </thead>
                                <tbody data-bind="foreach: teams">
                                    {jobSummaries.map((i, key) => (
                                        <tr key={i['jobSummaryId']}>
                                            <td>{i['proppantName']}</td>
                                            <td>{i['lofaName']}</td>
                                            <td>{i['sandPo']}</td>
                                            <td>{i['transportationPo']}</td>
                                            <td width={250}>
                                                <input
                                                    inputMode="decimal"
                                                    name="plannedTonnage"
                                                    type="text"
                                                    onChange={(event: any) => {
                                                        validatePlannedTonnage(event.target);
                                                        handleOnChange(
                                                            i['jobSummaryId'],
                                                            parseFloat(event.target.value),
                                                        );
                                                    }}
                                                    maxLength={9}
                                                />
                                                <div className="DailyTonnageModal-ErrorMessage"></div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            ''
                        )}
                    </Row>
                </Row>
            </div>
            <div className="DailyTonnageModal-ErrorMessage">{fieldErrors.plannedTonnageError}</div>
            <Modal.Footer className="DailyTonnageModal-Footer">
                <CustomButton title="Cancel" isBordered onClick={onCancel}></CustomButton>
                <CustomButton
                    title="Save"
                    isDisabled={isSaveDisabled}
                    onClick={() => {
                        setIsSaveDisabled(true);
                        onSave();
                    }}
                ></CustomButton>
            </Modal.Footer>
        </Modal>
    );
};

export default DailyTonnageModal;

